<template>
  <div class="container-fluid">
    <!-- Table [start] -->
    <div class="card">
      <div class="card-header">
        <div class="row align-items-center">
          <div class="col-sm-6 col-lg-8 col-xxl-6">
            <h4 class="header-title">{{ $t('installationComponents.listTitle') }}</h4>
          </div>
          <!-- Same grid as the rooms tab: wider where the button shows its label -->
          <div class="col-sm-6 col-lg-4 col-xxl-6 mt-2 mt-sm-0">
            <div class="d-flex">
              <input type="text" id="searchComponent" class="form-control form-control-sm" :placeholder="$t('common.search')"
                aria-controls="components-table" />
              <button type="button" id="components-licenses" class="btn ms-2 btn-outline-primary text-nowrap" :title="$t('installationComponents.manageLicenses')" @click="licenseModal.show(facilityId)">
                <i class="mdi mdi-key-outline"></i><span class="d-none d-xxl-inline-block ms-1">{{ $t('installationComponents.manageLicenses') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="card-body pt-0">
        <div class="component-treeview-legend tree-components-legend">
          <div class="tree-cell component-name">{{ $t('installationComponents.legend.component') }}</div>
          <div class="tree-cell component-type">{{ $t('installationComponents.legend.type') }}</div>
          <div class="tree-cell component-state">{{ $t('installationComponents.legend.state') }}</div>
          <div class="tree-cell component-action">{{ $t('installationComponents.legend.actions') }}</div>
        </div>
        <div id="jstree-components" ref="tree" class="component-treeview tree-components">
          <ul>
            <li v-for="hub in hubs" :key="hub.id" data-jstree='{ "icon" : "mdi mdi-server-network text-primary", "opened" : true }'>
              <div class="tree-row">
                <div class="tree-cell component-name fw-bold text-primary">
                  <TreeButton :path="hub.path()" :label="hub.label" type="link" />
                </div>
                <div class="tree-cell component-type">{{ hub.equipement_type }}</div>
                <div class="tree-cell component-state">
                  <strong v-if="hub.hub_state === 1" class="text-success">{{ $t('common.connected') }}</strong>
                  <strong v-else class="text-danger">{{ $t('common.disconnected') }}</strong>
                  <button v-if="hub.stateSinceText" type="button" class="btn btn-link p-0 ms-1 align-baseline text-muted state-info"
                    :title="hub.stateSinceText" :aria-label="hub.stateSinceText"
                    :data-title="hub.label" :data-text="hub.stateSinceText" :data-active="hub.hub_state === 1 ? 'true' : 'false'">
                    <i class="mdi mdi-information-outline"></i>
                  </button>
                </div>
                <div class="tree-cell component-action">
                  <TreeButton :path="hub.path()" />
                </div>
              </div>
              <ul>
                <li v-for="integration in hub.integrations" :key="integration.key" :data-jstree="integration.jstreeData">
                  <div class="tree-row">
                    <div class="tree-cell component-name fw-bold text-dark">
                      <TreeButton :path="integration.path(facilityId)" :label="integration.label" type="link" />
                    </div>
                    <div class="tree-cell component-type">{{ integration.label }}</div>
                    <div class="tree-cell component-state">
                      <template v-if="integration.has_state">
                        <strong v-if="integration.isActive" class="text-success">{{ $t('common.active') }}</strong>
                        <strong v-else class="text-danger">{{ $t('common.inactive') }}</strong>
                      </template>
                    </div>
                    <div class="tree-cell component-action">
                      <TreeButton :path="integration.path(facilityId)" />
                    </div>
                  </div>
                </li>
                <li v-for="reader in hub.readers" :key="reader.key(hub.id)"
                  data-jstree='{ "icon" : "mdi mdi-key-wireless text-success" }'>
                  <div class="tree-row">
                    <div class="tree-cell component-name fw-bold text-dark">
                      <TreeButton :path="reader.path(facilityId, hub.id)" :label="reader.label" type="link" />
                    </div>
                    <div class="tree-cell component-type">{{ reader.typeLabel }}</div>
                    <div class="tree-cell component-state">
                      <strong :class="reader.isStateActive ? 'text-success' : 'text-danger'">{{ reader.stateLabel }}</strong>
                      <button v-if="reader.stateSinceText" type="button" class="btn btn-link p-0 ms-1 align-baseline text-muted state-info"
                        :title="reader.stateSinceText" :aria-label="reader.stateSinceText"
                        :data-title="reader.label" :data-text="reader.stateSinceText" :data-active="reader.isStateActive ? 'true' : 'false'">
                        <i class="mdi mdi-information-outline"></i>
                      </button>
                    </div>
                    <div class="tree-cell component-action">
                      <TreeButton :path="reader.path(facilityId, hub.id)" />
                    </div>
                  </div>
                </li>
                <li v-for="module in hub.extensions" :key="module.key(hub.id)"
                  data-jstree='{ "icon" : "mdi mdi-key-wireless text-success" }'>
                  <div class="tree-row">
                    <div class="tree-cell component-name fw-bold text-dark">
                      <TreeButton :path="module.path(facilityId, hub.id)" :label="module.label" type="link" />
                    </div>
                    <div class="tree-cell component-type">{{ module.type_as_string }}</div>
                    <div class="tree-cell component-state">
                      <strong v-if="module.module_state === 1" class="text-success">{{ $t('common.connected') }}</strong>
                      <strong v-else class="text-danger">{{ $t('common.disconnected') }}</strong>
                    </div>
                    <div class="tree-cell component-action">
                      <TreeButton :path="module.path(facilityId, hub.id)" />
                    </div>
                  </div>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <!-- Table [end] -->
    <StateInfoModal ref="stateInfoModal" />
    <FacilityLicenseModal ref="licenseModal" />
  </div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { useRouter, useRoute } from 'vue-router';
import TreeButton from '@/components/treeButton';
import StateInfoModal from '@/modals/stateInfoModal.vue';
import FacilityLicenseModal from '@/modals/facilityLicenseModal.vue';
import 'jstree/dist/themes/default/style.min.css';
import 'jstree';
import $ from 'jquery';

export default {
  name: "InstallationEditComponents",
  components: {
    TreeButton,
    StateInfoModal,
    FacilityLicenseModal,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const tree = ref(null);
    const facilityId = parseInt(route.query.id);
    const userStore = useUserStore();
    const hubs = userStore.getFacilityHubs(facilityId);
    const stateInfoModal = ref(null);
    const licenseModal = ref(null);
    let treeInstance = null;

    onMounted(() => {
      treeInstance = $(tree.value).jstree({
        plugins: ["search"],
        core: {
          themes: {
            responsive: false
          }
        },
        search: {
          show_only_matches: true,
          show_only_matches_children: true
        }
      });

      $(tree.value).on('click', '.tree-button', (event) => {
        const jsonURI = $(event.currentTarget).attr('data-url');
        const uri = JSON.parse(jsonURI);
        router.push({path: uri.path, query: uri.params});
      });

      $(tree.value).on('click', '.state-info', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const button = event.currentTarget;
        stateInfoModal.value.show(button.dataset.title, button.dataset.text, button.dataset.active === 'true');
      });

      let searchTimeout = null;

      document.getElementById("searchComponent").addEventListener("keyup", function () {
        let searchText = this.value.trim().toLowerCase();
        let treeInstance = $('#jstree-components').jstree(true);

        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(function () {
          treeInstance.search(searchText);
        }, 250);

        $("#jstree-components").find(".highlighted").removeClass("highlighted");

        if (searchText.length > 0) {
          setTimeout(function () {
            let nodes = $("#jstree-components").find(".jstree-search");

            nodes.each(function () {
              let node = $(this);

              if (node.text().toLowerCase().includes(searchText)) {
                node.addClass("highlighted");
              }

              let parents = node.parents(".jstree-node");
              treeInstance.open_node(parents);
            });
          }, 300);
        }
      });
    });

    onBeforeUnmount(() => {
      if (treeInstance) {
        treeInstance.jstree("destroy").empty();
      }
    });

    return {
      tree,
      hubs,
      facilityId,
      stateInfoModal,
      licenseModal,
    }
  },
}
</script>
